import { yupResolver } from '@hookform/resolvers/yup';
import React, { useRef, useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import * as yup from 'yup';
import Button from '../../../../components/Button';
import { Container, Form, Row, Column } from '../../../../components/commons';
import Input from '../../../../components/Input';
import CheckboxInput from '../../../../components/Input/CheckboxInput';
import Modal from '../../../../components/Modals/Modal';
import useToast from '../../../../hooks/useToast';
import { ApiIntegration } from '../../../../interfaces/ApiIntegrations';
import { useCreateApiIntegration, useUpdateApiIntegration } from '../../../../services/apiIntegrations.service';
import useDocumentTypes from '../../../../services/documentTypes.service';
import { theme } from '../../../../styles/theme';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import Select from 'react-select';

export interface ModalHandle {
  openFormModal: (initialData?: ApiIntegration) => void;
}

type ModalRef = React.ElementRef<typeof Modal>;

const schema = yup.object().shape({
  name: yup.string().required('Nome é obrigatório'),
  url: yup.string().required('URL é obrigatória').url('Deve ser uma URL válida'),
  documentTypeMappings: yup.array().of(
    yup.object().shape({
      documentType: yup.string().required('Tipo de Documento é obrigatório'),
      keys: yup.array().of(
        yup.object().shape({
          name: yup.string().required('Nome da chave é obrigatório'),
          value: yup.string().required('Valor da chave é obrigatório'),
        })
      ),
    })
  ),
});

const IntegrationModal: React.ForwardRefRenderFunction<ModalHandle, {}> = ({}, ref) => {
  const [initialData, setInitialData] = useState<ApiIntegration | null>(null);
  const toast = useToast();
  const modalRef = useRef<ModalRef>(null);

  const createMutation = useCreateApiIntegration();
  const updateMutation = useUpdateApiIntegration();
  const { data: documentTypesData } = useDocumentTypes(1, 1000, '', 'name');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ApiIntegration>({
    resolver: yupResolver(schema) as any,
    defaultValues: { name: '', url: '', documentTypeMappings: [] },
  });

  const { fields: mappings, append: appendMapping, remove: removeMapping } = useFieldArray({
    control,
    name: 'documentTypeMappings',
  });

  React.useImperativeHandle(ref, () => ({
    openFormModal(data?: ApiIntegration) {
      if (data) {
        setInitialData(data);
        reset(data);
      } else {
        setInitialData(null);
        reset({ name: '', url: '', documentTypeMappings: [] });
      }
      modalRef.current?.openModal();
    },
  }));

  const onSubmit = async (formData: ApiIntegration) => {
    try {
      const cleanPayload = {
        name: formData.name,
        url: formData.url,
        documentTypeMappings: formData.documentTypeMappings?.map(mapping => ({
          documentType: mapping.documentType,
          keys: mapping.keys?.map(key => ({
            name: key.name,
            value: key.value,
            isPeriod: key.isPeriod,
          })) || []
        })) || []
      };

      if (initialData?._id) {
        await updateMutation.mutateAsync({ id: initialData._id, payload: cleanPayload });
        toast.successToast('Integração atualizada com sucesso!');
      } else {
        await createMutation.mutateAsync(cleanPayload as any);
        toast.successToast('Integração criada com sucesso!');
      }
      modalRef.current?.closeModal();
    } catch (error) {
      toast.errorToast('Erro ao salvar integração!');
    }
  };

  const documentTypeOptions = documentTypesData?.data?.map((dt) => ({
    value: dt._id,
    label: dt.name,
  })) || [];

  return (
    <Modal title={initialData ? 'Editar Integração' : 'Cadastrar Integração'} ref={modalRef}>
      <Container style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px' }}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Input
            type='text'
            label='Nome da Integração'
            placeholder='Ex: Integração Contábil X'
            error={errors?.name}
            control={control}
            name='name'
          />

          <Input
            type='text'
            label='URL do Endpoint'
            placeholder='https://api.exemplo.com/webhook'
            error={errors?.url}
            control={control}
            name='url'
          />

          <div style={{ marginTop: 20 }}>
            <Row justify='space-between' align='center' style={{ marginBottom: 10 }}>
              <h3 style={{ fontSize: 16, color: theme.colors.title }}>Tipos de Documentos Vinculados</h3>
              <Button
                variantType='block'
                label='Adicionar Vínculo'
                type='button'
                color='success'
                onClick={() => appendMapping({ documentType: '', keys: [] })}
              />
            </Row>

            {mappings.map((mapping, index) => (
              <div key={mapping.id} style={{ border: '1px solid #ddd', padding: 15, borderRadius: 8, marginBottom: 15 }}>
                <Row justify='space-between' align='center'>
                  <div style={{ flex: 1, marginRight: 10 }}>
                    <label style={{ display: 'block', marginBottom: 5, fontSize: 14, color: theme.colors.text }}>Tipo de Documento</label>
                    <MappingDocumentTypeSelect
                      control={control}
                      index={index}
                      options={documentTypeOptions}
                      error={errors?.documentTypeMappings?.[index]?.documentType}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeMapping(index)}
                    style={{ background: 'transparent', border: 'none', color: theme.colors.danger, cursor: 'pointer', marginTop: 20 }}
                    title="Remover Vínculo"
                  >
                    <FiTrash2 size={20} />
                  </button>
                </Row>

                <div style={{ marginTop: 15 }}>
                  <MappingKeys control={control} mappingIndex={index} errors={errors} />
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 20 }}>
            <Button
              label='Salvar Integração'
              loading={createMutation.isLoading || updateMutation.isLoading}
              type='submit'
              variantType='block'
              color='primary'
            />
          </div>
        </Form>
      </Container>
    </Modal>
  );
};

// Sub-component for keys
const MappingKeys = ({ control, mappingIndex, errors }: any) => {
  const { fields: keys, append: appendKey, remove: removeKey } = useFieldArray({
    control,
    name: `documentTypeMappings.${mappingIndex}.keys`,
  });

  return (
    <div>
      <Row justify='space-between' align='center' style={{ marginBottom: 10 }}>
        <h4 style={{ fontSize: 14, color: theme.colors.text }}>Chaves enviadas</h4>
        <button
          type="button"
          onClick={() => appendKey({ name: '', value: '' })}
          style={{ background: 'transparent', border: 'none', color: theme.colors.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <FiPlus /> Adicionar Chave
        </button>
      </Row>
      
      {keys.map((keyField, keyIndex) => (
        <Row key={keyField.id} align='center' style={{ gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <Input
              type='text'
              placeholder='Nome da Chave (Ex: token)'
              control={control}
              name={`documentTypeMappings.${mappingIndex}.keys.${keyIndex}.name`}
              error={errors?.documentTypeMappings?.[mappingIndex]?.keys?.[keyIndex]?.name}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Input
              type='text'
              placeholder='Valor'
              control={control}
              name={`documentTypeMappings.${mappingIndex}.keys.${keyIndex}.value`}
              error={errors?.documentTypeMappings?.[mappingIndex]?.keys?.[keyIndex]?.value}
            />
          </div>
          <div style={{ flex: 0.5, display: 'flex', alignItems: 'center' }}>
            <CheckboxInput
              control={control}
              type='checkbox'
              name={`documentTypeMappings.${mappingIndex}.keys.${keyIndex}.isPeriod`}
              label='É Período?'
            />
          </div>
          <button
            type="button"
            onClick={() => removeKey(keyIndex)}
            style={{ background: 'transparent', border: 'none', color: theme.colors.danger, cursor: 'pointer' }}
          >
            <FiTrash2 size={18} />
          </button>
        </Row>
      ))}
      {keys.length === 0 && <span style={{ fontSize: 12, color: '#999' }}>Nenhuma chave configurada.</span>}
    </div>
  );
};

// Custom wrapper for react-select using react-hook-form's Controller
const MappingDocumentTypeSelect = ({ control, index, options, error }: any) => {
  return (
    <>
      <Controller
        name={`documentTypeMappings.${index}.documentType`}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            options={options}
            value={options.find((c: any) => c.value === field.value)}
            onChange={(val: any) => field.onChange(val.value)}
            placeholder="Selecione..."
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        )}
      />
      {error && <span style={{ color: theme.colors.danger, fontSize: 12 }}>{error.message}</span>}
    </>
  );
};

export default React.forwardRef(IntegrationModal);
